import { useMemo } from "react";
import CreateEditNewsAlertModalDetails from "@/components/news-alerts/modals/CreateEditNewsAlertModalDetails";
import CreateEditNewsAlertModalSchedule from "@/components/news-alerts/modals/CreateEditNewsAlertModalSchedule";
import CreateEditNewsAlertsRecipientsField from "@/components/news-alerts/modals/CreateEditNewsAlertsRecipientsField";
import CreateEditNewsAlertsSectionsList from "@/components/news-alerts/modals/CreateEditNewsAlertsSectionsList";
import LogoUploadSection from "@/components/news-alerts/modals/shared/LogoUploadSection";
import { isValidEmail } from "@/components/news-alerts/newsAlertsHelpers";
import {
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
} from "@/components/ui/dialog";
import {
  closeCreateNewsAlertModal,
  removeNewsAlertLogo,
  setUploadedNewsAlertLogo,
} from "@/store/news-alerts/news-alerts.actions";
import useNewsAlertsStore from "@/store/news-alerts/news-alerts.slice";
import {
  createNewsAlert,
  generateNewsAlertPreview,
  updateNewsAlert,
  uploadNewsAlertLogo,
} from "@/store/news-alerts/news-alerts-async.actions";

const CreateNewsAlertModal = () => {
  const isOpen = useNewsAlertsStore(
    (state) => state.isCreateNewsAlertModalOpen,
  );
  const isCreatingNewsAlert = useNewsAlertsStore(
    (state) => state.isCreatingNewsAlert,
  );
  const isEditingNewsAlert = useNewsAlertsStore(
    (state) => state.isEditingNewsAlert,
  );
  const isGeneratingNewsAlertPreview = useNewsAlertsStore(
    (state) => state.isGeneratingNewsAlertPreview,
  );
  const isUploadingNewsAlertLogo = useNewsAlertsStore(
    (state) => state.isUploadingNewsAlertLogo,
  );
  const editNewsAlertId = useNewsAlertsStore((state) => state.editNewsAlertId);
  const logoPreviewUrl = useNewsAlertsStore(
    (state) => state.createEditNewsAlert.logoPreviewUrl,
  );
  const logoFileName = useNewsAlertsStore(
    (state) => state.createEditNewsAlert.logoFile?.name ?? null,
  );

  const createEditNewsAlert = useNewsAlertsStore(
    (state) => state.createEditNewsAlert,
  );

  const isFormValid = useMemo(() => {
    return (
      Boolean(createEditNewsAlert.alertName) &&
      createEditNewsAlert.alertName.length > 0 &&
      Array.from(createEditNewsAlert.recipients).every((email) =>
        isValidEmail(email),
      ) &&
      createEditNewsAlert.sections.size > 0
    );
  }, [createEditNewsAlert]);

  const handleLogoFileSelect = (file: File) => {
    setUploadedNewsAlertLogo(file);
    void uploadNewsAlertLogo();
  };

  return (
    <DialogOverlay isOpen={isOpen}>
      <DialogContent
        onClose={closeCreateNewsAlertModal}
        className="h-[calc(100vh-2rem)] w-[calc(75vw-2rem)]"
      >
        <DialogHeader
          title={editNewsAlertId ? "Edit news alert" : "Create news alert"}
          aria-label="create-news-alert-header"
        />

        <DialogBody>
          <div className="flex flex-col gap-6 px-24">
            <CreateEditNewsAlertModalDetails />
            <CreateEditNewsAlertModalSchedule />
            <CreateEditNewsAlertsRecipientsField />
            <CreateEditNewsAlertsSectionsList />
            <LogoUploadSection
              previewSrc={logoPreviewUrl}
              fileName={logoFileName}
              onFileSelect={handleLogoFileSelect}
              onRemove={removeNewsAlertLogo}
              isDisabled={isUploadingNewsAlertLogo}
            />
          </div>
        </DialogBody>
        <DialogFooter
          primaryLabel={editNewsAlertId ? "Update alert" : "Create alert"}
          secondaryLabel="Generate preview in new tab"
          secondaryAction={generateNewsAlertPreview}
          primaryAction={editNewsAlertId ? updateNewsAlert : createNewsAlert}
          isPending={isCreatingNewsAlert || isEditingNewsAlert}
          secondaryVariant="ghost"
          secondaryPending={isGeneratingNewsAlertPreview}
          primaryDisabled={!isFormValid}
        />
      </DialogContent>
    </DialogOverlay>
  );
};

export default CreateNewsAlertModal;
